'use client';
import React, { useState } from 'react';
import { Submission, SiteSettings } from '@/lib/api/types';

interface SubmissionTrackerProps {
  submissions: Submission[];
  onShowModal: (submissionId?: string) => void;
  siteConfig?: SiteSettings;
}

type TrackerStatus = 'submitted' | 'ondeck' | 'rated';

export default function SubmissionTracker({ submissions, onShowModal, siteConfig }: SubmissionTrackerProps) {
  // Sort submissions by timestamp (newest first)
  const sortedSubmissions = [...submissions].sort((a, b) => {
    const timeA = (a as any).timestamp || 0;
    const timeB = (b as any).timestamp || 0;
    return timeB - timeA;
  });

  // State to track which submission is currently selected
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string>(
    sortedSubmissions[0]?.id || ''
  );

  // submissions are already filtered to unreviewed in the parent component
  const isVisible = submissions && submissions.length > 0;

  // Get the currently selected submission
  const currentSubmission = sortedSubmissions.find(s => s.id === selectedSubmissionId) || sortedSubmissions[0];

  if (!currentSubmission) {
    return null;
  }

  // Determine the current status based on the selected submission
  const isRated = currentSubmission.rating || currentSubmission.specialRating;
  const hasResponse = currentSubmission.responseText || currentSubmission.responseDropdownValue;
  const isOnDeck = currentSubmission.favorite || hasResponse;

  const getStatus = (): TrackerStatus => {
    if (isRated) return 'rated';
    if (isOnDeck) return 'ondeck';
    return 'submitted';
  };
  const status = getStatus();

  // Format timestamp for display
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'Recent';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get theme colors with fallbacks to defaults
  const cardBgColor = siteConfig?.trackerCardBackgroundColor || '#1a1a1a';
  const cardTextColor = siteConfig?.trackerCardTextColor || '#ffffff';
  const barOffBgColor = siteConfig?.trackerBarOffStateColor || '#f5f5f5';
  const barOffTextColor = siteConfig?.trackerBarOffTextColor || '#aaa';
  const barOnTextColor = siteConfig?.trackerBarOnTextColor || '#ffffff';
  const gradientStart = siteConfig?.trackerBarGradientStart || '#FF6200';
  const gradientEnd = siteConfig?.trackerBarGradientEnd || '#A503C6';
  const ctaBgColor = siteConfig?.trackerCtaButtonBackgroundColor || '#ffffff';
  const ctaTextColor = siteConfig?.trackerCtaButtonTextColor || '#000000';

  // This logic now determines the text for the HEADER.
  let currentHeaderText = siteConfig?.progressPhaseOneDesc || "Your take has reached the studio";
  if (status === 'ondeck') {
    currentHeaderText = siteConfig?.progressPhaseTwoDesc || "Hey Hey! Someone thinks your take has merit";
  } else if (status === 'rated') {
    currentHeaderText = siteConfig?.progressPhaseThreeDesc || "Stephen A. reviewed your take!";
  }

  return (
    <div
      className={`sticky top-0 z-[5] mb-4 overflow-hidden bg-white p-1 transition-all duration-500 ease-in-out ${
        isVisible ? 'max-h-[350px] opacity-100' : 'max-h-0 opacity-0'
      } lg:relative lg:top-auto`}
    >
      {/* CORRECTED: The header now correctly uses the dynamic description text. */}
      <div 
        className="mx-auto w-[500px] max-w-[90%] rounded-2xl p-[15px] lg:max-w-[80%]"
        style={{ backgroundColor: cardBgColor }}
      >
        <div 
          className="mb-[5px] text-center text-base font-normal"
          style={{ color: cardTextColor }}
        >
          {currentHeaderText}
        </div>

        {/* Dropdown to select different submissions */}
        {sortedSubmissions.length > 1 && (
          <div className="mb-3">
            <select
              value={selectedSubmissionId}
              onChange={(e) => setSelectedSubmissionId(e.target.value)}
              className="w-full rounded-lg border border-[#444] px-3 py-2 text-sm focus:border-[#FFDE00] focus:outline-none"
              style={{ 
                backgroundColor: cardBgColor, 
                color: cardTextColor 
              }}
            >
              {sortedSubmissions.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {formatDate((sub as any).timestamp)} - {
                    (sub.cleaned_text || sub.original_text || '').substring(0, 40)
                  }...
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-2 flex overflow-hidden rounded-3xl border-2 border-[#1b1b1b]">
          <div 
            className="flex-1 p-[5px] text-center text-sm font-bold transition-all duration-[400ms]"
            style={{
              background: `linear-gradient(to bottom, ${gradientStart}, ${gradientEnd})`,
              color: barOnTextColor
            }}
          >
            <div>{siteConfig?.progressPhaseOneCopy || "SUBMITTED"}</div>
          </div>
          <div
            className="flex-1 border-l-2 border-[#ccc] p-[5px] text-center text-sm font-bold transition-all duration-[400ms]"
            style={
              status === 'ondeck' || status === 'rated'
                ? {
                    background: `linear-gradient(to bottom, ${gradientStart}, ${gradientEnd})`,
                    color: barOnTextColor
                  }
                : {
                    backgroundColor: barOffBgColor,
                    color: barOffTextColor
                  }
            }
          >
            <div>{siteConfig?.progressPhaseTwoCopy || "ON DECK"}</div>
          </div>
          <div
            className="flex-1 border-l-2 border-[#ccc] p-[5px] text-center text-sm font-bold transition-all duration-[400ms]"
            style={
              status === 'rated'
                ? {
                    background: `linear-gradient(to bottom, ${gradientStart}, ${gradientEnd})`,
                    color: barOnTextColor
                  }
                : {
                    backgroundColor: barOffBgColor,
                    color: barOffTextColor
                  }
            }
          >
            <div>{siteConfig?.progressPhaseThreeCopy || "RATED!"}</div>
          </div>
        </div>

        {/* CORRECTED: This section now correctly appears below the bar when status is 'rated' OR when in review with a response. */}
        {(status === 'rated' || (status === 'ondeck' && hasResponse)) && (
          <div>
            <p 
              className="text-[0.9rem] font-normal"
              style={{ color: cardTextColor }}
            >
              {siteConfig?.progressCheckRatingCopy || "See what Stephen A. had to say!"}
            </p>
            <button
              className="w-full cursor-pointer rounded-[30px] border-none px-8 py-4 text-base font-normal transition-all hover:bg-[#FFDE00]"
              style={{
                backgroundColor: ctaBgColor,
                color: ctaTextColor
              }}
              onClick={() => onShowModal(currentSubmission.id)}
            >
              {siteConfig?.progressCheckRatingCTA || "See Rating Card"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
